import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, orderBy, where, getDocs, Timestamp, doc, getDoc, documentId } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from './firebase';
import './ManageConversations.css';

interface User {
  name: string;
  email: string;
}

interface Conversation {
  id: string;
  conversationId: string;
  bookingId: string;
  providerId: string;
  customerId: string;
  participants?: string[];
  isActive: boolean;
  createdAt: Timestamp;
  lastMessageAt: Timestamp;
  lastMessage: string | null;
  completedAt?: Timestamp;
  unreadCountCustomer: number;
  unreadCountProvider: number;
  // Additional fields for display
  providerName?: string;
  customerName?: string;
}

const ManageConversations: React.FC = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [filteredConversations, setFilteredConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 50;
  
  // Filter states
  const [providerFilter, setProviderFilter] = useState<string>('');
  const [customerFilter, setCustomerFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [timeFilter, setTimeFilter] = useState<string>('all');

  const statusOptions = [
    { value: 'all', label: 'Tất cả' },
    { value: 'active', label: 'Đang hoạt động' },
    { value: 'completed', label: 'Đã kết thúc' }
  ];

  const timeOptions = [
    { value: 'all', label: 'Tất cả' },
    { value: 'today', label: 'Hôm nay' },
    { value: 'last7days', label: '7 ngày qua' },
    { value: 'last30days', label: '30 ngày qua' },
    { value: 'last90days', label: '90 ngày qua' }
  ];

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [conversations, providerFilter, customerFilter, statusFilter, timeFilter]);

  const fetchUserData = async (userId: string): Promise<User | null> => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        return userDoc.data() as User;
      }
      return null;
    } catch (err) {
      console.error(`Error fetching user ${userId}:`, err);
      return null;
    }
  };

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const conversationsRef = collection(db, 'conversations');

      // Build server-side constraints for status and time filters where possible
      const constraints: any[] = [];
      if (statusFilter !== 'all') {
        constraints.push(where('isActive', '==', statusFilter === 'active'));
      }
      if (timeFilter !== 'all') {
        const now = new Date();
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        let fromDate = startOfDay;
        if (timeFilter === 'today') {
          fromDate = startOfDay;
        } else if (timeFilter === 'last7days') {
          const d = new Date(startOfDay);
          d.setDate(d.getDate() - 7);
          fromDate = d;
        } else if (timeFilter === 'last30days') {
          const d = new Date(startOfDay);
          d.setDate(d.getDate() - 30);
          fromDate = d;
        } else if (timeFilter === 'last90days') {
          const d = new Date(startOfDay);
          d.setDate(d.getDate() - 90);
          fromDate = d;
        }
        constraints.push(where('lastMessageAt', '>=', Timestamp.fromDate(fromDate)));
      }

      let q;
      try {
        q = query(conversationsRef, ...constraints, orderBy('lastMessageAt', 'desc'));
      } catch (e) {
        q = query(conversationsRef, ...constraints);
      }
      const querySnapshot = await getDocs(q);
      
      const conversationsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Conversation[];

      // Batch user lookups: collect unique IDs and fetch via chunks
      const userIds = new Set<string>();
      conversationsData.forEach(c => {
        if (c.providerId) userIds.add(c.providerId);
        if (c.customerId) userIds.add(c.customerId);
      });

      const idList = Array.from(userIds);
      const chunk = <T,>(arr: T[], size: number) => {
        const out: T[][] = [];
        for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
        return out;
      };

      const batches = chunk(idList, 10);
      const userMap: Record<string, User> = {};
      for (const ids of batches) {
        const snap = await getDocs(query(collection(db, 'users'), where(documentId(), 'in', ids)));
        snap.forEach(d => {
          userMap[d.id] = d.data() as User;
        });
      }

      const conversationsWithUsers = conversationsData.map((conversation) => ({
        ...conversation,
        providerName: userMap[conversation.providerId]?.name || 'Không tìm thấy',
        customerName: userMap[conversation.customerId]?.name || 'Không tìm thấy'
      }));

      setConversations(conversationsWithUsers);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError('Không thể tải danh sách tin nhắn');
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...conversations];

    // Filter by provider name
    if (providerFilter.trim()) {
      const searchTerm = providerFilter.toLowerCase();
      filtered = filtered.filter(conv => 
        conv.providerName?.toLowerCase().includes(searchTerm)
      );
    }

    // Filter by customer name
    if (customerFilter.trim()) {
      const searchTerm = customerFilter.toLowerCase();
      filtered = filtered.filter(conv => 
        conv.customerName?.toLowerCase().includes(searchTerm)
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(conv => 
        statusFilter === 'active' ? conv.isActive : !conv.isActive
      );
    }

    // Filter by time
    if (timeFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const last7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const last90Days = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);

      filtered = filtered.filter(conv => {
        const lastMessageTime = conv.lastMessageAt.toDate();
        switch (timeFilter) {
          case 'today':
            return lastMessageTime >= today;
          case 'last7days':
            return lastMessageTime >= last7Days;
          case 'last30days':
            return lastMessageTime >= last30Days;
          case 'last90days':
            return lastMessageTime >= last90Days;
          default:
            return true;
        }
      });
    }

    setFilteredConversations(filtered);
  };

  const formatTimestamp = (timestamp: Timestamp) => {
    return timestamp.toDate().toLocaleString('vi-VN');
  };

  const truncateMessage = (message: string | null, maxLength: number = 30) => {
    if (!message) return 'Chưa có tin nhắn';
    if (message.length <= maxLength) return message;
    return `${message.substring(0, maxLength)}...`;
  };

  const handleBookingClick = (bookingId: string) => {
    navigate(`/admin/messages/${bookingId}`);
  };

  const getRowClass = (isActive: boolean): string => {
    return isActive ? 'row-active' : 'row-completed';
  };

  // Memoize filtered list and paginate
  const memoFilteredConversations = useMemo(() => {
    // reuse current filteredConversations state by re-running applyFilters logic
    let filtered = [...conversations];
    if (providerFilter.trim()) {
      const searchTerm = providerFilter.toLowerCase();
      filtered = filtered.filter(conv => conv.providerName?.toLowerCase().includes(searchTerm));
    }
    if (customerFilter.trim()) {
      const searchTerm = customerFilter.toLowerCase();
      filtered = filtered.filter(conv => conv.customerName?.toLowerCase().includes(searchTerm));
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(conv => statusFilter === 'active' ? conv.isActive : !conv.isActive);
    }
    if (timeFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const last7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const last90Days = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(conv => {
        const lastMessageTime = conv.lastMessageAt.toDate();
        switch (timeFilter) {
          case 'today':
            return lastMessageTime >= today;
          case 'last7days':
            return lastMessageTime >= last7Days;
          case 'last30days':
            return lastMessageTime >= last30Days;
          case 'last90days':
            return lastMessageTime >= last90Days;
          default:
            return true;
        }
      });
    }
    return filtered;
  }, [conversations, providerFilter, customerFilter, statusFilter, timeFilter]);

  const totalPages = useMemo(() => Math.ceil(memoFilteredConversations.length / recordsPerPage) || 1, [memoFilteredConversations.length]);
  const displayedConversations = useMemo(() => {
    const start = (currentPage - 1) * recordsPerPage;
    return memoFilteredConversations.slice(start, start + recordsPerPage);
  }, [memoFilteredConversations, currentPage]);

  useEffect(() => {
    setFilteredConversations(memoFilteredConversations);
    if (currentPage > totalPages) setCurrentPage(1);
  }, [memoFilteredConversations, currentPage, totalPages]);

  // Refetch when server-side filters change to reduce payload
  useEffect(() => {
    setCurrentPage(1);
    fetchConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, timeFilter]);

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner">Đang tải dữ liệu...</div>
      </div>
    );
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="manage-conversations">
      <div className="page-header">
        <h2>Quản lý tin nhắn</h2>
        <p className="page-subtitle">
          Quản lý và theo dõi các cuộc hội thoại giữa nhà cung cấp và khách hàng trong hệ thống
        </p>
      </div>

      <div className="filters">
        <div className="filter-group">
          <label>Nhà cung cấp</label>
          <input
            type="text"
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            placeholder="Tìm theo tên"
          />
        </div>

        <div className="filter-group">
          <label>Khách hàng</label>
          <input
            type="text"
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            placeholder="Tìm theo tên"
          />
        </div>

        <div className="filter-group">
          <label>Trạng thái</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Thời gian</label>
          <select
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
          >
            {timeOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <button className="refresh-button" onClick={fetchConversations}>
          Làm mới
        </button>
      </div>

      <div className="conversations-table">
        <table>
          <thead>
            <tr>
              <th>Booking ID</th>
              <th>Nhà cung cấp</th>
              <th>Khách hàng</th>
              <th>Trạng thái</th>
              <th>Tin nhắn cuối</th>
              <th>Thời gian</th>
            </tr>
          </thead>
          <tbody>
            {displayedConversations.map((conv, index) => (
              <tr key={conv.id} className={getRowClass(conv.isActive)}>
                <td>
                  <span 
                    className="booking-id-link"
                    onClick={() => handleBookingClick(conv.bookingId)}
                  >
                    {conv.bookingId}
                  </span>
                </td>
                <td>{conv.providerName}</td>
                <td>{conv.customerName}</td>
                <td>
                  <span className={`status-badge ${conv.isActive ? 'active' : 'completed'}`}>
                    {conv.isActive ? 'Đang hoạt động' : 'Đã kết thúc'}
                  </span>
                </td>
                <td title={conv.lastMessage || 'Chưa có tin nhắn'}>
                  {truncateMessage(conv.lastMessage)}
                </td>
                <td>{formatTimestamp(conv.lastMessageAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredConversations.length === 0 && (
          <div className="no-data">Không có cuộc hội thoại nào</div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <div className="pagination-info">
            Hiển thị {(currentPage - 1) * recordsPerPage + 1} - {Math.min(currentPage * recordsPerPage, memoFilteredConversations.length)} của {memoFilteredConversations.length} kết quả
          </div>
          <div className="pagination-controls">
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="pagination-btn">««</button>
            <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="pagination-btn">«</button>
            {(() => {
              const buttons = [] as React.ReactElement[];
              const start = Math.max(1, currentPage - 2);
              const end = Math.min(totalPages, currentPage + 2);
              for (let i = start; i <= end; i++) {
                buttons.push(
                  <button key={i} onClick={() => setCurrentPage(i)} disabled={i === currentPage} className={`pagination-btn ${i === currentPage ? 'active' : ''}`}>{i}</button>
                );
              }
              return buttons;
            })()}
            <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="pagination-btn">»</button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="pagination-btn">»»</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageConversations; 